import React from 'react';
import { Button } from '../../ui/Button';
import { Spinner } from '../../ui/Spinner';
import { Alert } from '../../ui/Alert';
import { RefreshCw, Calendar } from 'lucide-react';
import { TutorKpiCards } from './TutorKpiCards';
import { TutorTrendChart } from './TutorTrendChart';
import { TutorLastPracticed } from './TutorLastPracticed';
import { TutorCommunicationRubric } from './TutorCommunicationRubric';
import { useTutorAnalytics } from '../../../tutor/hooks/useTutorAnalytics';

interface TutorAnalyticsPanelProps {
  studentId?: string;
}

export const TutorAnalyticsPanel: React.FC<TutorAnalyticsPanelProps> = ({ studentId }) => {
  const { data, loading, error, days, setDays, refetch } = useTutorAnalytics({
    studentId,
    initialDays: 30,
  });

  const dayOptions = [7, 14, 30, 90];

  const handleRefresh = () => {
    refetch();
  };

  if (loading) {
    return (
      <div className="space-y-6">
        {/* Skeleton for KPI cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="animate-pulse">
              <div className="bg-gray-200 dark:bg-gray-700 rounded-xl p-6 h-24"></div>
            </div>
          ))}
        </div>

        {/* Skeleton for chart and last practiced */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="animate-pulse">
            <div className="bg-gray-200 dark:bg-gray-700 rounded-xl p-6 h-64"></div>
          </div>
          <div className="animate-pulse">
            <div className="bg-gray-200 dark:bg-gray-700 rounded-xl p-6 h-32"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="error" className="mb-6">
        {error}
      </Alert>
    );
  }

  if (!data) {
    return (
      <Alert variant="info" className="mb-6">
        No analytics data available
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      {/* Controls */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <Calendar className="w-5 h-5 text-app-text-secondary dark:text-gray-400" />
          <span className="text-sm font-medium text-app-text-secondary dark:text-gray-400">
            Period:
          </span>
          <div className="flex gap-1">
            {dayOptions.map((option) => (
              <Button
                key={option}
                variant={days === option ? 'primary' : 'secondary'}
                size="sm"
                onClick={() => setDays(option)}
                className="px-3 py-1 text-xs"
              >
                {option}d
              </Button>
            ))}
          </div>
        </div>

        <Button
          variant="secondary"
          size="sm"
          onClick={handleRefresh}
          disabled={loading}
          className="flex items-center gap-2"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* KPI Cards */}
      <TutorKpiCards data={data} />

      {/* Chart and Last Practiced */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <TutorTrendChart trend={data.trend} />
        <TutorLastPracticed data={data} />
      </div>

      <TutorCommunicationRubric speakingSessions={data.speaking_sessions} />
    </div>
  );
};